import React, { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { type ProfileInterface } from 'data-access/user/types/profile.interface'
import { dataAccessUser } from 'data-access/user/data-access-user'
import { authService } from './auth.service'
import { Loader } from 'ui/core/loader'
import { isJSON } from '../tool/is-json'

interface UserContextProps {
  user: ProfileInterface | null
  isLoading: boolean
}
// Create a context for the user provider
export const UserContext = createContext<UserContextProps>({ user: null, isLoading: false })

// User provider component
export const UserProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [user, setUser] = useState<ProfileInterface | null>(null)

  useEffect(() => {
    if (authService.isLoggedIn) {
      void loadUser()
    }
  }, [])

  const loadUser = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const userData = await dataAccessUser.getProfile()
      const cpv = userData.cpvNotificationList?.length
        ? isJSON(userData.cpvNotificationList)
          ? JSON.parse(userData.cpvNotificationList)
          : userData.cpvNotificationList
        : ''
      const parsedUser = {
        ...userData,
        cpvNotificationList: cpv
      }
      setUser(parsedUser)
      // TODO: Update test mode
    } catch (error: any) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <UserContext.Provider value={{ user, isLoading }}>
      {isLoading && <Loader />}
      {children}
    </UserContext.Provider>
  )
}

export const useUserProfile = (): UserContextProps => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProvider')
  }
  return context
}
